import {equals, isNotNil} from 'ramda';

import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  automarketplEurotaxVariantsResponseSchema,
  type AutomarketplEurotaxVariantsResponse,
} from '@prompt-bridge/shared/validations';

import {
  AutomarketplInspectionQuestions,
  AutomarketplInspectionQuestionsSchema,
  GetSalePresetSettingsApiArg,
  GetSalePresetSettingsApiResponse,
  DisablePublishCebiaSmartCodeUrlApiArg,
  DisablePublishCebiaSmartCodeUrlApiResponse,
  CreateSalePresetSettingsApiArg,
  CreateSalePresetSettingsApiResponse,
  DeleteSalePresetSettingsApiArg,
  DeleteSalePresetSettingsApiResponse,
  UpdateSalePresetSettingsApiArg,
  UpdateSalePresetSettingsApiResponse,
  EditGeneralSettingsApiArg,
  EditGeneralSettingsApiResponse,
  EditPlatformSettingsApiArg,
  EditPlatformSettingsApiResponse,
  EditWindshieldSettingsApiArg,
  EditWindshieldSettingsApiResponse,
  EnablePublishCebiaSmartCodeUrlApiArg,
  EnablePublishCebiaSmartCodeUrlApiResponse,
  GetCebiaSmartCodeUrlConfigApiArg,
  GetCebiaSmartCodeUrlConfigApiResponse,
  GetBusinessCaseDocumentsSettingsApiResponse,
  PutBusinessCaseDocumentsSettingsApiArg,
  PutBusinessCaseDocumentsSettingsApiResponse,
  GetGeneralSettingsApiArg,
  GetGeneralSettingsApiResponse,
  GetGenerationsApiArg,
  GetGenerationsApiResponse,
  GetPlatformSettingsApiArg,
  GetPlatformSettingsApiResponse,
  GetSalesPlatformsApiArg,
  GetSalesPlatformsApiRes,
  GetAdvertisementPreflightApiArg,
  GetAdvertisementPreflightApiResponse,
  GetAdvertisementPreflightApiResponseSchema,
  GetAdvertisementPreviewApiArg,
  GetAdvertisementPreviewApiResponse,
  GetAdvertisementPreviewApiResponseSchema,
  GetAdvertisementPreviewPhotosApiArg,
  GetAdvertisementPreviewPhotosApiResponse,
  GetAdvertisementPreviewPhotosApiResponseSchema,
  VehicleAdAttributeApiArg,
  GetVehicleAdAttributesApiRes,
  MobileDeRefdataOptionsResponseBody,
  GetVersionsApiArg,
  GetVersionsApiResponse,
  GetWindshieldSettingsApiArg,
  GetWindshieldSettingsApiResponse,
  UpdateVehicleIsInSaleApiArg,
  UpdateVehicleIsInSaleApiRes,
  UpdateVehicleOnAllPlatformsApiArg,
  UpdateVehicleOnAllPlatformsApiResponse,
  UpdateVehicleOnPlatformApiArg,
  UpdateVehicleOnPlatformApiResponse,
  CreatePurchaseBusinessCaseApiArg,
  CreatePurchaseBusinessCaseApiResponse,
  CreateSaleBusinessCaseApiArg,
  CreateSaleBusinessCaseApiResponse,
  UpdateKeyLocationApiArg,
  UpdateKeyLocationApiResponse,
  GetDigitalCertificateThemesApiResponse,
  CreateSalePresetSettingsV2ApiArg,
  CreateSalePresetSettingsV2ApiResponse,
  DeleteSalePresetSettingsV2ApiArg,
  DeleteSalePresetSettingsV2ApiResponse,
  GetSalePresetSettingsListApiArg,
  GetSalePresetSettingsListApiResponse,
  GetSalePresetSettingsV2ApiArg,
  GetSalePresetSettingsV2ApiResponse,
  UpdateSalePresetSettingsV2ApiArg,
  UpdateSalePresetSettingsV2ApiResponse,
  GetQualitySealsApiArg,
  GetQualitySealsApiResponse,
  GetSalePresetQualitySealImpactApiArg,
  GetSalePresetQualitySealImpactApiResponse,
  SetSalesFinanceSettingsApiArg,
  SetSalesFinanceSettingsApiResponse,
  GetSalesFinanceSettingsApiResponse,
  CreateBusinessCaseActivityApiArg,
  CreateBusinessCaseActivityApiResponse,
  GetBusinessCaseActivityListApiArg,
  GetBusinessCaseActivityListApiResponse,
  GetVehicleActivityListApiArg,
  GetVehicleActivityListApiResponse,
  CreateInterestActivityApiArg,
  CreateInterestActivityApiResponse,
  GetInterestActivityListApiArg,
  GetInterestActivityListApiResponse,
  PatchSalesActivityApiResponse,
  PatchSalesActivityApiArg,
  DeleteSalesActivityApiResponse,
  DeleteSalesActivityApiArg,
  GetActiveQuickPresetsApiResponse,
  GetActiveQuickPresetsApiArg,
  DisablePlatformForVehicleApiResponse,
  DisablePlatformForVehicleApiArg,
  EnablePlatformForVehicleApiResponse,
  EnablePlatformForVehicleApiArg,
  RepeatLastActionApiResponse,
  RepeatLastActionApiArg,
  GetWindshieldPaperTemplatesApiResponse,
  GetWindshieldPaperTemplatesApiArg,
  RenderWindshieldPaperDocumentApiResponse,
  RenderWindshieldPaperDocumentApiArg,
  GenerateVgpCertificateApiArg,
} from './types';

export const salesApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    updateVehicleIsInSale: build.mutation<UpdateVehicleIsInSaleApiRes, UpdateVehicleIsInSaleApiArg>(
      {
        query: ({
          vehicleId,
          publish,
          validateMandatoryFields,
          publishMobileDeViaAdvertisingBridge,
          publishVgpViaAdvertisingBridge,
          publishOtomotoViaAdvertisingBridge,
          publishAutocarisViaAdvertisingBridge,
          publishMercedesNaSkladeViaAdvertisingBridge,
          publishHyundaiPromiseViaAdvertisingBridge,
        }) => ({
          url: `/dms/v2/sales/vehicle/${vehicleId}/${publish ? 'publish' : 'unpublish'}`,
          method: 'PUT',
          params: {
            validateMandatoryFields,
            publishMobileDeViaAdvertisingBridge,
            publishVgpViaAdvertisingBridge,
            publishOtomotoViaAdvertisingBridge,
            publishAutocarisViaAdvertisingBridge,
            publishMercedesNaSkladeViaAdvertisingBridge,
            publishHyundaiPromiseViaAdvertisingBridge,
          },
        }),
        invalidatesTags: ['AdGeneralSettings', 'AdPlatformSettings', 'AdWindshieldSettings'],
      }
    ),
    getVehicleAdAttributes: build.query<GetVehicleAdAttributesApiRes, VehicleAdAttributeApiArg>({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/platform-attributes`,
      }),
      providesTags: ['AdGeneralSettings'],
    }),
    getMobileDeModelRangeOptions: build.query<
      MobileDeRefdataOptionsResponseBody,
      VehicleAdAttributeApiArg
    >({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/model-ranges`,
      }),
      // Refdata response includes `suggestion` derived from vehicle data + DE_MODEL_RANGE
      // custom field; invalidate on any vehicle edit so the form picks up fresh suggestion
      // without a page reload.
      providesTags: (_result, _error, {vehicleId}) => [{type: 'Vehicle', id: vehicleId}],
    }),
    getMobileDeTrimLineOptions: build.query<
      MobileDeRefdataOptionsResponseBody,
      VehicleAdAttributeApiArg
    >({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/trim-lines`,
      }),
      // Refdata response includes `suggestion` matched against vehicle's `trim` field;
      // invalidate on any vehicle edit so the form picks up fresh suggestion without
      // a page reload.
      providesTags: (_result, _error, {vehicleId}) => [{type: 'Vehicle', id: vehicleId}],
    }),
    getSalesPlatforms: build.query<GetSalesPlatformsApiRes, GetSalesPlatformsApiArg>({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/advertisements`,
      }),
      providesTags: (_result, _error, {vehicleId}) => [{type: 'AdGeneralSettings', id: vehicleId}],
      // Invalidate tags if any platform status has changed
      async onQueryStarted(arg, {dispatch, getCacheEntry, queryFulfilled}) {
        try {
          const oldPlatforms = getCacheEntry().data;
          const {data: newPlatforms} = await queryFulfilled;

          if (Array.isArray(oldPlatforms) && Array.isArray(newPlatforms)) {
            const toStatusDict = (list: GetSalesPlatformsApiRes | undefined) =>
              Object.fromEntries((list ?? []).map((p) => [p.platform.code, p.status] as const));

            if (!equals(toStatusDict(oldPlatforms), toStatusDict(newPlatforms))) {
              dispatch(
                salesApi.util.invalidateTags(['AdPlatformSettings', 'AdWindshieldSettings'])
              );
            }
          }
        } catch {} // Query failed, no need to handle status comparison
      },
    }),
    getAdvertisementPreflight: build.query<
      GetAdvertisementPreflightApiResponse,
      GetAdvertisementPreflightApiArg
    >({
      query: ({
        vehicleId,
        publishVgpViaAdvertisingBridge,
        publishOtomotoViaAdvertisingBridge,
        publishAutocarisViaAdvertisingBridge,
        publishMercedesNaSkladeViaAdvertisingBridge,
        publishHyundaiPromiseViaAdvertisingBridge,
      }) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/advertisements/preflight`,
        params: {
          publishVgpViaAdvertisingBridge,
          publishOtomotoViaAdvertisingBridge,
          publishAutocarisViaAdvertisingBridge,
          publishMercedesNaSkladeViaAdvertisingBridge,
          publishHyundaiPromiseViaAdvertisingBridge,
        },
      }),
      extraOptions: {
        responseSchema: GetAdvertisementPreflightApiResponseSchema,
      },
      providesTags: (_result, _error, {vehicleId}) => [
        {type: 'Vehicle', id: vehicleId},
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
        'SaleVehicleAlbumPhotos',
        'SaleVehicleAlbums',
      ],
    }),
    getAdvertisementPreview: build.query<
      GetAdvertisementPreviewApiResponse,
      GetAdvertisementPreviewApiArg
    >({
      query: ({vehicleId, platformCode, language}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/advertisements/preview`,
        params: {platformCode, language},
      }),
      extraOptions: {
        responseSchema: GetAdvertisementPreviewApiResponseSchema,
      },
      providesTags: (_result, _error, {vehicleId}) => [
        {type: 'Vehicle', id: vehicleId},
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    getAdvertisementPreviewPhotos: build.query<
      GetAdvertisementPreviewPhotosApiResponse,
      GetAdvertisementPreviewPhotosApiArg
    >({
      query: ({vehicleId, platformCode}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/advertisements/preview/photos/${platformCode}`,
      }),
      extraOptions: {
        responseSchema: GetAdvertisementPreviewPhotosApiResponseSchema,
      },
      providesTags: (_result, _error, {vehicleId}) => [
        {type: 'Vehicle', id: vehicleId},
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
        'SaleVehicleAlbumPhotos',
        'SaleVehicleAlbums',
        'WatermarkList',
      ],
    }),
    updateVehicleOnAllPlatforms: build.mutation<
      UpdateVehicleOnAllPlatformsApiResponse,
      UpdateVehicleOnAllPlatformsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/updateOnAllPlatforms`,
        method: 'PUT',
      }),
    }),
    updateVehicleOnPlatform: build.mutation<
      UpdateVehicleOnPlatformApiResponse,
      UpdateVehicleOnPlatformApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}/update`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    editGeneralSettings: build.mutation<EditGeneralSettingsApiResponse, EditGeneralSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}`,
        method: 'PATCH',
        body: queryArg.patchVehicleAdvertisingSettingsRequestBody,
      }),
      invalidatesTags: ['AdGeneralSettings', 'AdPlatformSettings', 'AdWindshieldSettings'],
    }),
    getGeneralSettings: build.query<GetGeneralSettingsApiResponse, GetGeneralSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}`,
      }),
      providesTags: ['AdGeneralSettings'],
    }),
    editWindshieldSettings: build.mutation<
      EditWindshieldSettingsApiResponse,
      EditWindshieldSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/windshield`,
        method: 'PATCH',
        body: queryArg.patchVehicleWindshieldAdvertisingSettingsRequestBody,
      }),
      invalidatesTags: ['AdWindshieldSettings'],
    }),
    getWindshieldSettings: build.query<
      GetWindshieldSettingsApiResponse,
      GetWindshieldSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/windshield`,
      }),
      providesTags: ['AdWindshieldSettings'],
    }),
    getAutomarketplInspectionQuestions: build.query<
      AutomarketplInspectionQuestions,
      {vehicleId: string}
    >({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/platform/automarketpl/inspection-questions`,
      }),
      transformResponse: (response) => AutomarketplInspectionQuestionsSchema.parse(response),
      providesTags: (_result, _error, {vehicleId}) => [
        {type: 'Vehicle', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    getAutomarketplEurotaxVariants: build.query<
      AutomarketplEurotaxVariantsResponse,
      {vehicleId: string}
    >({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/platform/automarketpl/eurotax-variants`,
      }),
      transformResponse: (response) => automarketplEurotaxVariantsResponseSchema.parse(response),
      providesTags: (_result, _error, {vehicleId}) => [
        {type: 'Vehicle', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    editPlatformSettings: build.mutation<
      EditPlatformSettingsApiResponse,
      EditPlatformSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}`,
        method: 'PATCH',
        body: queryArg.vehiclePlatformAdvertisingSettingsRequestBody,
      }),
      invalidatesTags: ['AdPlatformSettings'],
    }),
    getPlatformSettings: build.query<GetPlatformSettingsApiResponse, GetPlatformSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}`,
      }),
      providesTags: ['AdPlatformSettings'],
    }),
    getBusinessCaseDocumentsSettings: build.query<
      GetBusinessCaseDocumentsSettingsApiResponse,
      void
    >({
      query: () => ({
        url: `/dms/v1/settings/business-case/documents`,
      }),
      providesTags: ['BusinessCaseSettings'],
    }),
    putBusinessCaseDocumentsSettings: build.mutation<
      PutBusinessCaseDocumentsSettingsApiResponse,
      PutBusinessCaseDocumentsSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/business-case/documents`,
        method: 'PUT',
        body: queryArg.businessCaseDocumentsSettingsRequestBody,
      }),
      invalidatesTags: ['BusinessCaseSettings'],
    }),
    getSalesFinanceSettings: build.query<GetSalesFinanceSettingsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/settings/sales/finance`,
      }),
      providesTags: ['SalesFinanceSettings'],
    }),
    setSalesFinanceSettings: build.mutation<
      SetSalesFinanceSettingsApiResponse,
      SetSalesFinanceSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sales/finance`,
        method: 'PUT',
        body: queryArg.salesFinanceSettingsRequestBody,
      }),
      invalidatesTags: ['SalesFinanceSettings'],
    }),
    createPurchaseBusinessCase: build.mutation<
      CreatePurchaseBusinessCaseApiResponse,
      CreatePurchaseBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/purchase`,
        method: 'POST',
        body: queryArg.createPurchaseBusinessCaseRequestBody,
      }),
      invalidatesTags: () => ['SaleVehicleActions'],
    }),
    createSaleBusinessCase: build.mutation<
      CreateSaleBusinessCaseApiResponse,
      CreateSaleBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/sale`,
        method: 'POST',
        body: queryArg.createSaleBusinessCaseRequestBody,
        ...(queryArg.xBranch && {headers: {'X-Branch': queryArg.xBranch}}),
      }),
      invalidatesTags: (_, __, arg) => [
        {
          type: 'VehicleLastActiveBC',
          id: arg.createSaleBusinessCaseRequestBody.vehicleToSaleId,
        },
        {
          type: 'BusinessCaseCountByVehicle',
          id: arg.createSaleBusinessCaseRequestBody.vehicleToSaleId,
        },
      ],
    }),
    getSalePresetSettings: build.query<
      GetSalePresetSettingsApiResponse,
      GetSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset/${queryArg.salePresetId}`,
      }),
    }),
    getSalePresetSettingsList: build.query<
      GetSalePresetSettingsListApiResponse,
      GetSalePresetSettingsListApiArg
    >({
      query: () => ({
        url: `/dms/v1/settings/sale-preset`,
      }),
      providesTags: [{type: 'SalePreset', id: 'LIST'}],
    }),
    createSalePresetSettings: build.mutation<
      CreateSalePresetSettingsApiResponse,
      CreateSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset`,
        method: 'POST',
        body: queryArg.createSalePresetSettingsRequestBody,
      }),
    }),
    deleteSalePresetSettings: build.mutation<
      DeleteSalePresetSettingsApiResponse,
      DeleteSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'DELETE',
      }),
    }),
    updateSalePresetSettings: build.mutation<
      UpdateSalePresetSettingsApiResponse,
      UpdateSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'PUT',
        body: queryArg.updateSalePresetSettingsRequestBody,
      }),
    }),

    createSalePresetSettingsV2: build.mutation<
      CreateSalePresetSettingsV2ApiResponse,
      CreateSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset`,
        method: 'POST',
        body: queryArg.createSalePresetSettingsV2RequestBody,
      }),
      invalidatesTags: invalidateOnSuccess([{type: 'SalePreset', id: 'LIST'}]),
    }),
    deleteSalePresetSettingsV2: build.mutation<
      DeleteSalePresetSettingsV2ApiResponse,
      DeleteSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'DELETE',
        body: queryArg.deleteSalePresetSettingsV2RequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: DeleteSalePresetSettingsV2ApiArg) => [
          {type: 'SalePreset', id: queryArg.salePresetId},
          {type: 'SalePreset', id: 'LIST'},
        ]
      ),
    }),
    getSalePresetSettingsV2: build.query<
      GetSalePresetSettingsV2ApiResponse,
      GetSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SalePreset', id: queryArg.salePresetId},
      ],
    }),
    updateSalePresetSettingsV2: build.mutation<
      UpdateSalePresetSettingsV2ApiResponse,
      UpdateSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'PUT',
        body: queryArg.updateSalePresetSettingsV2RequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(
        (_result, queryArg: UpdateSalePresetSettingsV2ApiArg) => [
          {type: 'SalePreset', id: queryArg.salePresetId},
          {type: 'SalePreset', id: 'LIST'},
          ...(queryArg.updateSalePresetSettingsV2RequestBody.propagateQualitySeal
            ? [{type: 'SaleVehicle'} as const]
            : []),
        ]
      ),
    }),
    getQualitySeals: build.query<GetQualitySealsApiResponse, GetQualitySealsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales/quality-seal?${new URLSearchParams([
          ...(queryArg.makeKeys ?? []).map((makeKey) => ['makeKeys[]', makeKey]),
          ...(isNotNil(queryArg.vehicleType) ? [['vehicleType', queryArg.vehicleType]] : []),
        ])}`,
      }),
    }),
    getSalePresetQualitySealImpact: build.query<
      GetSalePresetQualitySealImpactApiResponse,
      GetSalePresetQualitySealImpactApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}/quality-seal-impact`,
        params:
          queryArg.defaultQualitySealKey == null
            ? undefined
            : {defaultQualitySealKey: queryArg.defaultQualitySealKey},
      }),
    }),
    updateKeyLocation: build.mutation<UpdateKeyLocationApiResponse, UpdateKeyLocationApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/keyLocation`,
        method: 'PATCH',
        body: queryArg.updateVehicleKeyLocationRequestBody,
      }),
      invalidatesTags: (_, __, arg) => [{type: 'Vehicle', id: arg.vehicleId}],
    }),
    disablePublishCebiaSmartCodeUrl: build.mutation<
      DisablePublishCebiaSmartCodeUrlApiResponse,
      DisablePublishCebiaSmartCodeUrlApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/disable-publish-cebia-smart-code-url`,
        method: 'PUT',
      }),
      async onQueryStarted({vehicleId}, {dispatch, queryFulfilled}) {
        // OPTIMISTIC RESPONSE
        const patchResult = dispatch(
          salesApi.util.updateQueryData(
            'getPublishCebiaSmartCodeUrlConfig',
            {vehicleId},
            (draft) => {
              draft.publishCebiaSmartCodeUrl = false;
            }
          )
        );
        await queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (result, error, queryArg) => [
        {type: 'CebiaSmartCodeUrlConfig', id: queryArg.vehicleId},
      ],
    }),
    enablePublishCebiaSmartCodeUrl: build.mutation<
      EnablePublishCebiaSmartCodeUrlApiResponse,
      EnablePublishCebiaSmartCodeUrlApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/enable-publish-cebia-smart-code-url`,
        method: 'PUT',
      }),
      async onQueryStarted({vehicleId}, {dispatch, queryFulfilled}) {
        // OPTIMISTIC RESPONSE
        const patchResult = dispatch(
          salesApi.util.updateQueryData(
            'getPublishCebiaSmartCodeUrlConfig',
            {vehicleId},
            (draft) => {
              draft.publishCebiaSmartCodeUrl = true;
            }
          )
        );
        await queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (result, error, queryArg) => [
        {type: 'CebiaSmartCodeUrlConfig', id: queryArg.vehicleId},
      ],
    }),
    getPublishCebiaSmartCodeUrlConfig: build.query<
      GetCebiaSmartCodeUrlConfigApiResponse,
      GetCebiaSmartCodeUrlConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/cebia-smart-code-url-config`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CebiaSmartCodeUrlConfig', id: queryArg.vehicleId},
      ],
    }),
    getGenerations: build.query<GetGenerationsApiResponse, GetGenerationsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/generations`,
      }),
    }),
    getVersions: build.query<GetVersionsApiResponse, GetVersionsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/versions`,
      }),
    }),
    getDigitalCertificateThemes: build.query<GetDigitalCertificateThemesApiResponse, void>({
      query: () => ({
        url: `/dms/v2/sales/publishing/digital-certificate-themes`,
      }),
    }),
    getActiveQuickPresets: build.query<
      GetActiveQuickPresetsApiResponse,
      GetActiveQuickPresetsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/active-quick-preset`,
      }),
    }),
    getBusinessCaseActivityList: build.query<
      GetBusinessCaseActivityListApiResponse,
      GetBusinessCaseActivityListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/sales-activity`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
      ],
    }),
    getVehicleActivityList: build.query<
      GetVehicleActivityListApiResponse,
      GetVehicleActivityListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/sales-activity`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'VehicleActivity', id: queryArg.vehicleId},
      ],
    }),
    createBusinessCaseActivity: build.mutation<
      CreateBusinessCaseActivityApiResponse,
      CreateBusinessCaseActivityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/sales-activity`,
        method: 'POST',
        body: queryArg.createSalesActivityRequestBody,
      }),
      invalidatesTags: (_, __, arg) => [
        {type: 'BusinessCaseActivity', id: arg.businessCaseId},
        {type: 'BusinessCaseDetail', id: arg.businessCaseId},
      ],
    }),
    createInterestActivity: build.mutation<
      CreateInterestActivityApiResponse,
      CreateInterestActivityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/sales-activity`,
        method: 'POST',
        body: queryArg.createSalesActivityRequestBody,
      }),
      // Logging a call can open a New interest on the backend and make the caller its owner.
      invalidatesTags: (_, __, arg) => [
        {type: 'InterestActivity', id: arg.interestId},
        {type: 'Interest', id: arg.interestId},
        'participation',
        'InterestCount',
        'PersonnelOverviewInterest',
      ],
    }),
    deleteSalesActivity: build.mutation<DeleteSalesActivityApiResponse, DeleteSalesActivityApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales-activity/${queryArg.salesActivityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['InterestActivity', 'BusinessCaseActivity'],
    }),
    patchSalesActivity: build.mutation<PatchSalesActivityApiResponse, PatchSalesActivityApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales-activity/${queryArg.salesActivityId}`,
        method: 'PATCH',
        body: queryArg.patchSalesActivityRequestBody,
      }),
      invalidatesTags: () => ['InterestActivity', 'BusinessCaseActivity'],
    }),
    getInterestActivityList: build.query<
      GetInterestActivityListApiResponse,
      GetInterestActivityListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/sales-activity`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'InterestActivity', id: queryArg.interestId},
      ],
    }),
    disablePlatformForVehicle: build.mutation<
      DisablePlatformForVehicleApiResponse,
      DisablePlatformForVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platforms/${queryArg.platformCode}/disable`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    enablePlatformForVehicle: build.mutation<
      EnablePlatformForVehicleApiResponse,
      EnablePlatformForVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platforms/${queryArg.platformCode}/enable`,
        method: 'PUT',
        params: {
          publishMobileDeViaAdvertisingBridge: queryArg.publishMobileDeViaAdvertisingBridge,
          publishVgpViaAdvertisingBridge: queryArg.publishVgpViaAdvertisingBridge,
          publishOtomotoViaAdvertisingBridge: queryArg.publishOtomotoViaAdvertisingBridge,
          publishAutocarisViaAdvertisingBridge: queryArg.publishAutocarisViaAdvertisingBridge,
          publishMercedesNaSkladeViaAdvertisingBridge:
            queryArg.publishMercedesNaSkladeViaAdvertisingBridge,
          publishHyundaiPromiseViaAdvertisingBridge:
            queryArg.publishHyundaiPromiseViaAdvertisingBridge,
        },
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    repeatLastAction: build.mutation<RepeatLastActionApiResponse, RepeatLastActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}/repeat`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    getWindshieldPaperTemplates: build.query<
      GetWindshieldPaperTemplatesApiResponse,
      GetWindshieldPaperTemplatesApiArg
    >({
      query: () => ({
        url: `/dms/v1/sales/document/windshield-paper/templates`,
      }),
      providesTags: ['DocumentTemplates'],
    }),
    renderWindshieldPaperDocument: build.mutation<
      RenderWindshieldPaperDocumentApiResponse,
      RenderWindshieldPaperDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales/document/windshield-paper`,
        method: 'PUT',
        body: queryArg.windshieldPaperRequestBody,
      }),
    }),
    generateVgpCertificate: build.mutation<void, GenerateVgpCertificateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/generate-vgp-certificate`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useUpdateVehicleIsInSaleMutation,
  useGetVehicleAdAttributesQuery,
  useGetMobileDeModelRangeOptionsQuery,
  useGetMobileDeTrimLineOptionsQuery,
  useGetSalesPlatformsQuery,
  useLazyGetSalesPlatformsQuery,
  useGetAdvertisementPreflightQuery,
  useGetAdvertisementPreviewQuery,
  useGetAdvertisementPreviewPhotosQuery,
  useUpdateVehicleOnAllPlatformsMutation,
  useUpdateVehicleOnPlatformMutation,
  useEditGeneralSettingsMutation,
  useGetGeneralSettingsQuery,
  useEditWindshieldSettingsMutation,
  useGetWindshieldSettingsQuery,
  useGetAutomarketplInspectionQuestionsQuery,
  useGetAutomarketplEurotaxVariantsQuery,
  useEditPlatformSettingsMutation,
  useGetPlatformSettingsQuery,
  useDisablePublishCebiaSmartCodeUrlMutation,
  useEnablePublishCebiaSmartCodeUrlMutation,
  useGetPublishCebiaSmartCodeUrlConfigQuery,
  useGetVersionsQuery,
  useCreateSalePresetSettingsMutation,
  useDeleteSalePresetSettingsMutation,
  useUpdateSalePresetSettingsMutation,
  useGetSalePresetSettingsQuery,
  useGetSalePresetSettingsListQuery,
  useLazyGetSalePresetSettingsListQuery,
  useCreateSalePresetSettingsV2Mutation,
  useDeleteSalePresetSettingsV2Mutation,
  useGetSalePresetSettingsV2Query,
  useLazyGetSalePresetSettingsV2Query,
  useUpdateSalePresetSettingsV2Mutation,
  useGetQualitySealsQuery,
  useLazyGetQualitySealsQuery,
  useGetSalePresetQualitySealImpactQuery,
  useLazyGetSalePresetQualitySealImpactQuery,
  useGetGenerationsQuery,
  useGetActiveQuickPresetsQuery,
  useGetDigitalCertificateThemesQuery,
  useGetBusinessCaseDocumentsSettingsQuery,
  useCreatePurchaseBusinessCaseMutation,
  useCreateSaleBusinessCaseMutation,
  useUpdateKeyLocationMutation,
  usePutBusinessCaseDocumentsSettingsMutation,
  useGetSalesFinanceSettingsQuery,
  useSetSalesFinanceSettingsMutation,
  useGetBusinessCaseActivityListQuery,
  useGetVehicleActivityListQuery,
  useCreateBusinessCaseActivityMutation,
  useCreateInterestActivityMutation,
  useGetInterestActivityListQuery,
  useDeleteSalesActivityMutation,
  usePatchSalesActivityMutation,
  useDisablePlatformForVehicleMutation,
  useEnablePlatformForVehicleMutation,
  useRepeatLastActionMutation,
  useGetWindshieldPaperTemplatesQuery,
  useRenderWindshieldPaperDocumentMutation,
  useGenerateVgpCertificateMutation,
} = salesApi;
